import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from '../../components/AppToast';
import GenericFormTabPo from '../poDetails/tabs/GenericFormTabPo';
import { getallfacility } from '../../services/facility/facility';
import {
  createStockTransferOut,
  fetchStockTransferOutItemBySku
} from '../../services/stockTransferOut/stockTransferOut';
import { formatToInputDate } from '../../utils/dateFormatter';
import './add-stock-transfer-out.css';

const FACILITY_LIST_PAGE_SIZE = 500;

/** Build a multi-line postal address from API facility shape (same fields as Facility list / sales order). */
function formatFacilityAddress(facility) {
  if (!facility) return '';
  const addr = facility.Facility_Address || {};
  const line1 = [addr.address1, addr.address2, addr.address3, addr.address4].filter(Boolean).join(', ');
  const line2 = [
    addr.city,
    addr.state,
    addr.pincode || addr.postalCode || addr.zip || addr.postal
  ].filter(Boolean).join(', ');
  const line3 = addr.country || '';
  return [line1, line2, line3].filter(Boolean).join('\n');
}

/** Match facility row by Mongo `_id` or `Facility_ID` (header / API may use either). */
function matchFacility(facilities, mongoOrCode) {
  if (!mongoOrCode || !Array.isArray(facilities)) return null;
  const key = String(mongoOrCode).trim();
  if (!key) return null;
  return (
    facilities.find((f) => String(f._id) === key) ||
    facilities.find((f) => String(f.Facility_ID) === key) ||
    null
  );
}

/** Default source warehouse: header selection, then default flag, then first row. */
function resolveDefaultSourceFacility(facilities, selectedFacility) {
  if (!Array.isArray(facilities) || facilities.length === 0) return null;
  if (selectedFacility) {
    const byId = selectedFacility.id ? matchFacility(facilities, selectedFacility.id) : null;
    if (byId) return byId;
    const byCode = selectedFacility.value ? matchFacility(facilities, selectedFacility.value) : null;
    if (byCode) return byCode;
  }
  return facilities.find((f) => f.defaultWh === true) || facilities[0];
}

/** Item line fields — aligned with Purchase Order item details (plus available qty for transfer). */
const STO_ITEM_FIELDS = [
  { name: 'lineNo', label: 'Line Number (Auto)', readOnly: true, disabled: true, placeholder: '00001' },
  { name: 'skuId', label: 'SKU ID', placeholder: '', hasSearch: true, required: true },
  { name: 'description', label: 'Description', placeholder: '', required: true, readOnly: true },
  { name: 'itemCube', label: 'Item Volume ', type: 'number', placeholder: '0' },
  { name: 'itemWeight', label: 'Item Weight ', type: 'number', placeholder: '0' },
  { name: 'pack', label: 'Pack', placeholder: 'STD', hasSearch: false },
  {
    name: 'uom',
    label: 'UOM',
    type: 'select',
    options: [
      { value: 'EA', label: 'EA' },
      { value: 'PCS', label: 'PCS' },
      { value: 'KG', label: 'KG' },
      { value: 'LBS', label: 'LBS' },
      { value: 'M', label: 'M' },
      { value: 'FT', label: 'FT' }
    ],
    placeholder: 'EA'
  },
  { name: 'availableQty', label: 'Available Qty', type: 'number', placeholder: '0', readOnly: true },
  { name: 'orderedQty', label: 'Transfer Qty', placeholder: '0', type: 'number', required: true },
  { name: 'unitPrice', label: 'Price per item', placeholder: '0', type: 'number', required: true },
  { name: 'tax', label: 'Tax %', placeholder: '0', type: 'number', required: true },
  {
    name: 'qcRequired',
    label: 'QC Required',
    type: 'select',
    options: [
      { value: 'No', label: 'No' },
      { value: 'Yes', label: 'Yes' }
    ],
    placeholder: 'No'
  }
];

const getDefaultItemDraft = () => ({
  skuId: '',
  description: '',
  itemCube: '',
  itemWeight: '',
  pack: 'STD',
  uom: 'EA',
  availableQty: '',
  orderedQty: '',
  unitPrice: '',
  tax: '',
  qcRequired: 'No'
});

const calculateItemTotal = ({ orderedQty, unitPrice, tax }) => {
  const qty = Number(orderedQty) || 0;
  const price = Number(unitPrice) || 0;
  const taxPct = Number(tax) || 0;
  const subtotal = qty * price;
  return Number((subtotal * (1 + taxPct / 100)).toFixed(2));
};

/** Match saved STO document to a facility row for dropdown selection. */
function resolveStoFacilityMongoId(doc, facilities, role) {
  if (!doc || !Array.isArray(facilities)) return '';
  const idField = role === 'source' ? 'sourceLocationId' : 'destinationLocationId';
  const codeField = role === 'source' ? 'sourceLocation' : 'destinationLocation';
  const mongo = doc[idField];
  if (mongo && facilities.some((f) => f._id === mongo)) return mongo;

  const code = doc[codeField];
  if (code != null && String(code).trim()) {
    const s = String(code).trim();
    const byCode = facilities.find((f) => f.Facility_ID === s || f._id === s);
    if (byCode) return byCode._id;
  }

  const nameHint = doc[codeField];
  if (typeof nameHint === 'string' && nameHint.trim()) {
    const byName = facilities.find(
      (f) => (f.Facility_Name || '').trim().toLowerCase() === nameHint.trim().toLowerCase()
    );
    if (byName) return byName._id;
  }

  return '';
}

function mapDocItemsToState(items) {
  const arr = Array.isArray(items) ? items : [];
  return arr.map((item, idx) => {
    const orderedQty = item.orderedQty ?? item.transferQty ?? item.qty ?? 0;
    const unitPrice = item.unitPrice ?? 0;
    const tax = item.tax ?? 0;
    const rawLine = item.lineNo ?? idx + 1;
    const lineNo = typeof rawLine === 'number' ? rawLine : Number(rawLine) || idx + 1;
    const totalAmount =
      item.totalAmount != null
        ? Number(item.totalAmount)
        : calculateItemTotal({ orderedQty, unitPrice, tax });
    return {
      id: String(item._id || item.id || `line-${idx}-${lineNo}`),
      lineNo,
      skuId: item.skuId ?? item.sku ?? '',
      description: item.description ?? '',
      itemCube: item.itemCube === undefined || item.itemCube === null ? '' : String(item.itemCube),
      itemWeight: item.itemWeight === undefined || item.itemWeight === null ? '' : String(item.itemWeight),
      pack: item.pack ?? 'STD',
      uom: item.uom ?? 'EA',
      availableQty:
        item.availableQty === undefined || item.availableQty === null ? '' : String(item.availableQty),
      orderedQty,
      unitPrice,
      tax,
      qcRequired: item.qcRequired ?? 'No',
      totalAmount
    };
  });
}

export default function AddStockTransferOut({
  onCancel,
  onSuccess,
  viewOnly = false,
  initialDocument = null
}) {
  const selectedFacility = useSelector((state) => state?.facility?.selectedFacility);
  const [facilities, setFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);
  /** Selected facility document `_id` (from `facility/fetch`). */
  const [sourceLocation, setSourceLocation] = useState('');
  const [destinationLocation, setDestinationLocation] = useState('');

  const [facilityAddress, setFacilityAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');

  const [remarks, setRemarks] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [productWeightGrams, setProductWeightGrams] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [noOfBoxes, setNoOfBoxes] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [items, setItems] = useState([]);
  const [itemDraft, setItemDraft] = useState(getDefaultItemDraft);
  const [editingItemId, setEditingItemId] = useState(null);
  const [formSubmissionAttempted, setFormSubmissionAttempted] = useState(false);
  const [saving, setSaving] = useState(false);

  const nextLineNumber = useMemo(() => {
    if (editingItemId) {
      const row = items.find((i) => i.id === editingItemId);
      return row ? row.lineNo : items.length + 1;
    }
    return items.length + 1;
  }, [items, editingItemId]);

  const itemFormData = useMemo(
    () => ({
      ...itemDraft,
      lineNo: String(nextLineNumber).padStart(5, '0')
    }),
    [itemDraft, nextLineNumber]
  );

  const fetchStoSkuDetails = useCallback(
    async (_fieldName, fieldValue) => {
      const sku = String(fieldValue ?? '').trim();
      if (!sku) return null;
      if (!sourceLocation) {
        toast.error('Please select source location before looking up SKU');
        return null;
      }
      try {
        return await fetchStockTransferOutItemBySku(sku, {
          sourceLocationId: sourceLocation
        });
      } catch (err) {
        toast.error(err?.message || 'Failed to load SKU details');
        return null;
      }
    },
    [sourceLocation]
  );

  useEffect(() => {
    if (viewOnly && initialDocument) return;
    let cancelled = false;
    (async () => {
      setFacilitiesLoading(true);
      try {
        const res = await getallfacility(0, FACILITY_LIST_PAGE_SIZE);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (cancelled) return;
        setFacilities(list);
        if (list.length > 0) {
          const sourceFac = resolveDefaultSourceFacility(list, selectedFacility);
          const sourceId = sourceFac?._id || list[0]._id;
          setSourceLocation(String(sourceId));
          setDestinationLocation('');
          setFacilityAddress(formatFacilityAddress(sourceFac || list[0]));
          setDestinationAddress('');
        } else {
          setSourceLocation('');
          setDestinationLocation('');
          setFacilityAddress('');
          setDestinationAddress('');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('facility/fetch failed', err);
          toast.error(typeof err === 'string' ? err : 'Failed to load facilities');
          setFacilities([]);
          setSourceLocation('');
          setDestinationLocation('');
          setFacilityAddress('');
          setDestinationAddress('');
        }
      } finally {
        if (!cancelled) setFacilitiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewOnly, initialDocument, selectedFacility?.id, selectedFacility?.value]);

  useEffect(() => {
    if (!viewOnly || !initialDocument) return;
    let cancelled = false;
    (async () => {
      setFacilitiesLoading(true);
      try {
        const res = await getallfacility(0, FACILITY_LIST_PAGE_SIZE);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (cancelled) return;
        setFacilities(list);
        const doc = initialDocument;
        const src = resolveStoFacilityMongoId(doc, list, 'source');
        const dst = resolveStoFacilityMongoId(doc, list, 'destination');
        setSourceLocation(src);
        setDestinationLocation(dst);
        setFacilityAddress(
          (doc.sourceAddress && String(doc.sourceAddress)) ||
            (src ? formatFacilityAddress(list.find((f) => f._id === src)) : '') ||
            ''
        );
        setDestinationAddress(
          (doc.destinationAddress && String(doc.destinationAddress)) ||
            (dst ? formatFacilityAddress(list.find((f) => f._id === dst)) : '') ||
            ''
        );
        setRemarks(doc.remarks ?? '');
        setExpectedDeliveryDate(
          formatToInputDate(doc.expectedDeliveryDate ?? doc.expectedDate, false) || ''
        );
        setProductWeightGrams(
          doc.productWeightGrams != null && doc.productWeightGrams !== ''
            ? String(doc.productWeightGrams)
            : ''
        );
        setTransporterName(doc.transporterName ?? '');
        setNoOfBoxes(
          doc.noOfBoxes != null && doc.noOfBoxes !== '' ? String(doc.noOfBoxes) : ''
        );
        setGrossWeight(
          doc.grossWeight != null && doc.grossWeight !== '' ? String(doc.grossWeight) : ''
        );
        setItems(mapDocItemsToState(doc.items ?? doc.lineItems));
        setItemDraft(getDefaultItemDraft());
        setEditingItemId(null);
      } catch (err) {
        if (!cancelled) {
          console.error('facility/fetch failed (view)', err);
          toast.error(typeof err === 'string' ? err : 'Failed to load facilities');
          setFacilities([]);
        }
      } finally {
        if (!cancelled) setFacilitiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewOnly, initialDocument, selectedFacility?.id, selectedFacility?.value]);

  useEffect(() => {
    if (viewOnly) return;
    if (!sourceLocation || facilities.length === 0) return;
    const selected = matchFacility(facilities, sourceLocation);
    if (selected) setFacilityAddress(formatFacilityAddress(selected));
  }, [viewOnly, sourceLocation, facilities]);

  useEffect(() => {
    if (viewOnly) return;
    if (!destinationLocation || facilities.length === 0) return;
    const selected = matchFacility(facilities, destinationLocation);
    if (selected) setDestinationAddress(formatFacilityAddress(selected));
  }, [viewOnly, destinationLocation, facilities]);

  const handleSourceLocationChange = (e) => {
    const id = e.target.value;
    setSourceLocation(id);
    const selected = matchFacility(facilities, id);
    setFacilityAddress(selected ? formatFacilityAddress(selected) : '');
  };

  const handleDestinationLocationChange = (e) => {
    const id = e.target.value;
    setDestinationLocation(id);
    const selected = matchFacility(facilities, id);
    setDestinationAddress(selected ? formatFacilityAddress(selected) : '');
  };

  const totals = useMemo(() => {
    const totalQty = items.reduce((sum, item) => sum + (Number(item.orderedQty) || 0), 0);
    const totalAmount = items.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
    return {
      totalQty,
      totalAmount: Number(totalAmount.toFixed(2))
    };
  }, [items]);

  const handleItemDraftChange = (e) => {
    const { name, value } = e.target;
    if (name === 'lineNo') return;
    const numericFields = new Set([
      'availableQty',
      'orderedQty',
      'unitPrice',
      'tax',
      'itemCube',
      'itemWeight'
    ]);
    if (numericFields.has(name) && value && !/^-?\d*(\.\d*)?$/.test(value)) {
      return;
    }
    setItemDraft((prev) => ({ ...prev, [name]: value }));
    setFormSubmissionAttempted(false);
  };

  const validateItemDraft = () => {
    const errs = [];
    if (!itemDraft.skuId || String(itemDraft.skuId).trim() === '') errs.push('SKU ID is required');
    if (!itemDraft.description || String(itemDraft.description).trim() === '') errs.push('Description is required');
    const tq = Number(itemDraft.orderedQty);
    if (itemDraft.orderedQty === '' || Number.isNaN(tq) || tq < 0) errs.push('Transfer qty must be zero or greater');
    const up = Number(itemDraft.unitPrice);
    if (itemDraft.unitPrice === '' || Number.isNaN(up) || up < 0) errs.push('Price per item is required');
    const tx = Number(itemDraft.tax);
    if (itemDraft.tax === '' || Number.isNaN(tx) || tx < 0) errs.push('Tax % is required');
    return errs;
  };

  const draftToRow = (lineNo, id) => {
    const availableQty = Number(itemDraft.availableQty) || 0;
    const orderedQty = Number(itemDraft.orderedQty) || 0;
    const unitPrice = Number(itemDraft.unitPrice) || 0;
    const tax = Number(itemDraft.tax) || 0;
    const base = {
      id,
      lineNo,
      skuId: String(itemDraft.skuId).trim(),
      description: String(itemDraft.description).trim(),
      itemCube: itemDraft.itemCube === '' ? '' : Number(itemDraft.itemCube),
      itemWeight: itemDraft.itemWeight === '' ? '' : Number(itemDraft.itemWeight),
      pack: itemDraft.pack || 'STD',
      uom: itemDraft.uom || 'EA',
      availableQty,
      orderedQty,
      unitPrice,
      tax,
      qcRequired: itemDraft.qcRequired || 'No',
      totalAmount: calculateItemTotal({ orderedQty, unitPrice, tax })
    };
    return base;
  };

  const handleAddOrUpdateLine = (e) => {
    e.preventDefault();
    const validationErrors = validateItemDraft();
    if (validationErrors.length > 0) {
      setFormSubmissionAttempted(true);
      validationErrors.forEach((msg) => toast.error(msg));
      return;
    }

    if (editingItemId) {
      const existing = items.find((i) => i.id === editingItemId);
      const lineNo = existing ? existing.lineNo : nextLineNumber;
      const updated = draftToRow(lineNo, editingItemId);
      setItems((prev) => prev.map((item) => (item.id === editingItemId ? updated : item)));
      setEditingItemId(null);
    } else {
      const id = `item-${Date.now()}-${items.length}`;
      const newRow = draftToRow(items.length + 1, id);
      setItems((prev) => [...prev, newRow]);
    }

    setItemDraft(getDefaultItemDraft());
    setFormSubmissionAttempted(false);
  };

  const handleEditLine = (row) => {
    setEditingItemId(row.id);
    setItemDraft({
      skuId: row.skuId ?? '',
      description: row.description ?? '',
      itemCube: row.itemCube === undefined || row.itemCube === null ? '' : String(row.itemCube),
      itemWeight: row.itemWeight === undefined || row.itemWeight === null ? '' : String(row.itemWeight),
      pack: row.pack ?? 'STD',
      uom: row.uom ?? 'EA',
      availableQty: row.availableQty === undefined || row.availableQty === null ? '' : String(row.availableQty),
      orderedQty: row.orderedQty === undefined || row.orderedQty === null ? '' : String(row.orderedQty),
      unitPrice: row.unitPrice === undefined || row.unitPrice === null ? '' : String(row.unitPrice),
      tax: row.tax === undefined || row.tax === null ? '' : String(row.tax),
      qcRequired: row.qcRequired ?? 'No'
    });
    setFormSubmissionAttempted(false);
  };

  const handleCancelLineEdit = () => {
    setEditingItemId(null);
    setItemDraft(getDefaultItemDraft());
    setFormSubmissionAttempted(false);
  };

  const removeItem = (itemId) => {
    setItems((prev) =>
      prev
        .filter((item) => item.id !== itemId)
        .map((item, index) => ({ ...item, lineNo: index + 1 }))
    );
    if (editingItemId === itemId) {
      handleCancelLineEdit();
    }
  };

  const handleSave = async () => {
    if (viewOnly) return;
    if (!sourceLocation) {
      toast.error('Please select source location');
      return;
    }

    if (!destinationLocation) {
      toast.error('Please select destination location');
      return;
    }

    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const sourceFac = matchFacility(facilities, sourceLocation);
    const destFac = matchFacility(facilities, destinationLocation);

    const populatedItems = items.map((item) => ({
      lineNo: item.lineNo,
      skuId: item.skuId,
      description: item.description,
      itemCube: item.itemCube === '' || item.itemCube === undefined ? null : Number(item.itemCube),
      itemWeight: item.itemWeight === '' || item.itemWeight === undefined ? null : Number(item.itemWeight),
      pack: item.pack,
      uom: item.uom,
      availableQty: Number(item.availableQty) || 0,
      orderedQty: Number(item.orderedQty) || 0,
      unitPrice: Number(item.unitPrice) || 0,
      tax: Number(item.tax) || 0,
      qcRequired: item.qcRequired ?? 'No',
      totalAmount: Number(item.totalAmount) || 0
    }));

    const payload = {
      sourceLocation: sourceFac?.Facility_ID || sourceLocation,
      sourceLocationId: sourceFac?._id,
      sourceAddress: facilityAddress,
      destinationLocation: destFac?.Facility_ID || destinationLocation,
      destinationLocationId: destFac?._id,
      destinationAddress,
      totalTransferOutQty: totals.totalQty,
      totalTransferOutAmount: totals.totalAmount,
      remarks,
      expectedDeliveryDate,
      productWeightGrams: Number(productWeightGrams) || 0,
      transporterName,
      noOfBoxes: Number(noOfBoxes) || 0,
      grossWeight: Number(grossWeight) || 0,
      items: populatedItems
    };

    setSaving(true);
    try {
      await createStockTransferOut(payload);
      toast.success('Stock transfer out created successfully');
      onSuccess?.();
      onCancel?.();
    } catch {
      // HTTP errors are toasted by the axios interceptor; non-HTTP failures are rare here
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sto-create-root">
      <div className="sto-section">
        <div className="sto-section-header">
          <i className="bi bi-signpost-2" aria-hidden />
          Transfer route & locations
        </div>
        <div className="sto-section-body pb-0">
          <div className="sto-route-grid">
            <div className="sto-route-card">
              <div className="sto-route-card-title">
                <i className="bi bi-geo-alt text-primary" aria-hidden />
                Source
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="sto-source-loc">Source location</label>
                <select
                  id="sto-source-loc"
                  className="form-select"
                  value={facilitiesLoading ? '' : sourceLocation}
                  onChange={handleSourceLocationChange}
                  disabled={viewOnly || facilitiesLoading || facilities.length === 0}
                >
                  {facilitiesLoading ? (
                    <option value="">Loading facilities…</option>
                  ) : facilities.length === 0 ? (
                    <option value="">No facilities available</option>
                  ) : (
                    facilities.map((fac) => (
                      <option key={fac._id} value={String(fac._id)}>
                        {fac.Facility_Name || fac.Facility_ID || fac._id}
                        {fac.Facility_ID ? ` (${fac.Facility_ID})` : ''}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className="form-label" htmlFor="sto-source-addr">Source address</label>
                <textarea
                  id="sto-source-addr"
                  readOnly
                  className="form-control"
                  rows={2}
                  value={facilityAddress}
                />
              </div>
            </div>

            <div className="sto-route-arrow" aria-hidden>
              <i className="bi bi-arrow-right" />
            </div>

            <div className="sto-route-card sto-route-card--dest">
              <div className="sto-route-card-title">
                <i className="bi bi-geo-alt-fill text-primary" aria-hidden />
                Destination
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="sto-dest-loc">Destination location</label>
                <select
                  id="sto-dest-loc"
                  className="form-select"
                  value={facilitiesLoading ? '' : destinationLocation}
                  onChange={handleDestinationLocationChange}
                  disabled={viewOnly || facilitiesLoading || facilities.length === 0}
                >
                  {facilitiesLoading ? (
                    <option value="">Loading facilities…</option>
                  ) : facilities.length === 0 ? (
                    <option value="">No facilities available</option>
                  ) : (
                    <>
                      {!viewOnly && <option value="">— Select destination —</option>}
                      {facilities.map((fac) => (
                        <option key={fac._id} value={String(fac._id)}>
                          {fac.Facility_Name || fac.Facility_ID || fac._id}
                          {fac.Facility_ID ? ` (${fac.Facility_ID})` : ''}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="form-label" htmlFor="sto-dest-addr">Destination address</label>
                <textarea
                  id="sto-dest-addr"
                  readOnly
                  className="form-control"
                  rows={2}
                  value={destinationAddress}
                />
              </div>
            </div>
          </div>

          <div className="sto-summary-bar mt-3">
            <div className="sto-summary-item">
              <label>Total transfer qty</label>
              <div className="sto-summary-value">{totals.totalQty}</div>
            </div>
            <div className="sto-summary-item">
              <label>Total line amount</label>
              <div className="sto-summary-value">{totals.totalAmount.toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="sto-section">
        <div className="sto-section-header">
          <i className="bi bi-chat-left-text" aria-hidden />
          Notes
        </div>
        <div className="sto-section-body">
          <label className="form-label" htmlFor="sto-remarks">Remarks</label>
          <textarea
            id="sto-remarks"
            className="form-control"
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Optional notes for this transfer…"
            readOnly={viewOnly}
          />
        </div>
      </div>

      <div className="sto-section">
        <div className="sto-section-header">
          <i className="bi bi-truck" aria-hidden />
          Shipment & logistics
        </div>
        <div className="sto-section-body">
          <div className="row g-3">
            <div className="col-md-3 col-sm-6">
              <label className="form-label" htmlFor="sto-exp-delivery">Expected delivery</label>
              <input
                id="sto-exp-delivery"
                type="date"
                className="form-control"
                value={expectedDeliveryDate}
                onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                readOnly={viewOnly}
                disabled={viewOnly}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label" htmlFor="sto-prod-weight">Product weight (g)</label>
              <input
                id="sto-prod-weight"
                type="number"
                className="form-control"
                value={productWeightGrams}
                onChange={(e) => setProductWeightGrams(e.target.value)}
                min="0"
                step="1"
                readOnly={viewOnly}
                disabled={viewOnly}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label" htmlFor="sto-transporter">Transporter / courier</label>
              <input
                id="sto-transporter"
                type="text"
                className="form-control"
                value={transporterName}
                onChange={(e) => setTransporterName(e.target.value)}
                placeholder="Carrier name"
                readOnly={viewOnly}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label" htmlFor="sto-boxes">No. of boxes</label>
              <input
                id="sto-boxes"
                type="number"
                className="form-control"
                value={noOfBoxes}
                onChange={(e) => setNoOfBoxes(e.target.value)}
                min="0"
                readOnly={viewOnly}
                disabled={viewOnly}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label" htmlFor="sto-gross">Gross weight</label>
              <input
                id="sto-gross"
                type="number"
                className="form-control"
                value={grossWeight}
                onChange={(e) => setGrossWeight(e.target.value)}
                min="0"
                step="0.01"
                readOnly={viewOnly}
                disabled={viewOnly}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="sto-section">
        <div className="sto-section-header">
          <i className="bi bi-list-ul" aria-hidden />
          Item details
        </div>
        <div className="sto-section-body">
          <div className="sto-items-toolbar">
            <h6>
              <i className="bi bi-box-seam text-primary" aria-hidden />
              Line items
              <span className="badge bg-light text-dark border fw-normal ms-1">{items.length}</span>
            </h6>
            {editingItemId && !viewOnly && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancelLineEdit}>
                Cancel edit
              </button>
            )}
          </div>

          {!viewOnly && (
            <>
              <p className="text-muted small mb-3">
                Enter item details below (same layout as purchase order lines), then click Add to append to the table.
              </p>

              <form onSubmit={handleAddOrUpdateLine} autoComplete="off" className="sto-item-details-form">
                <GenericFormTabPo
                  fields={STO_ITEM_FIELDS}
                  formData={itemFormData}
                  onChange={handleItemDraftChange}
                  formSubmissionAttempted={formSubmissionAttempted}
                  formDataPo={{}}
                  fetchSkuDetails={fetchStoSkuDetails}
                />
                <div className="text-end mt-3 d-flex justify-content-end gap-2 flex-wrap">
                  {editingItemId && (
                    <button type="button" className="btn btn-outline-secondary px-4" onClick={handleCancelLineEdit}>
                      Cancel
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary px-5">
                    {editingItemId ? 'Update line' : 'Add'}
                  </button>
                </div>
              </form>
            </>
          )}

          {items.length === 0 ? (
            <div className="alert alert-light border mt-4 mb-0 small">
              {viewOnly
                ? 'No line items on this transfer.'
                : (
                  <>
                    No line items yet. Fill the form above and click <strong>Add</strong>.
                  </>
                )}
            </div>
          ) : (
            <div className={`sto-items-table-wrap ${viewOnly ? 'mt-3' : 'mt-4'}`}>
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>Line</th>
                    <th style={{ minWidth: 110 }}>SKU ID</th>
                    <th style={{ minWidth: 140 }}>Description</th>
                    <th style={{ width: 88 }}>Volume</th>
                    <th style={{ width: 88 }}>Weight</th>
                    <th style={{ width: 72 }}>UOM</th>
                    <th style={{ width: 80 }}>Pack</th>
                    <th style={{ width: 92 }}>QC</th>
                    <th style={{ width: 100 }}>Avail. qty</th>
                    <th style={{ width: 100 }}>Transfer qty</th>
                    <th style={{ width: 100 }}>Unit price</th>
                    <th style={{ width: 72 }}>Tax %</th>
                    <th style={{ width: 100 }}>Line total</th>
                    {!viewOnly && <th style={{ minWidth: 140 }} aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <span className="sto-line-badge">{item.lineNo}</span>
                      </td>
                      <td>{item.skuId}</td>
                      <td>{item.description}</td>
                      <td>{item.itemCube === '' || item.itemCube === undefined ? '—' : item.itemCube}</td>
                      <td>{item.itemWeight === '' || item.itemWeight === undefined ? '—' : item.itemWeight}</td>
                      <td>{item.uom}</td>
                      <td>{item.pack}</td>
                      <td>{item.qcRequired ?? '—'}</td>
                      <td>{item.availableQty}</td>
                      <td>{item.orderedQty}</td>
                      <td>{Number(item.unitPrice).toFixed(2)}</td>
                      <td>{item.tax}</td>
                      <td className="font-monospace">{Number(item.totalAmount).toFixed(2)}</td>
                      {!viewOnly && (
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleEditLine(item)}
                            >
                              <i className="bi bi-pencil me-1" aria-hidden />
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm btn-remove-line"
                              onClick={() => removeItem(item.id)}
                            >
                              <i className="bi bi-trash3 me-1" aria-hidden />
                              Remove
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="sto-footer-actions d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={saving}>
          {viewOnly ? 'Back' : 'Cancel'}
        </button>
        {!viewOnly && (
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || facilitiesLoading}>
            <i className="bi bi-check2-circle me-1" aria-hidden />
            {saving ? 'Saving…' : 'Save stock transfer out'}
          </button>
        )}
      </div>
    </div>
  );
}
